import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAdminSidebar } from "@/contexts/AdminSidebarContext";
import {
  LayoutDashboard, Users, Briefcase, Target, CheckCircle, DollarSign,
  CreditCard, MessageSquare, Mail, MailOpen, BarChart3, MapPin, Settings,
  Shield, FileText, Server, ScrollText, Star, Send, LogOut, X, Menu,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// badgeKey ties a nav item to a live count fetched below; badgeColor per ROUTE_MAP.
const NAV_SECTIONS = [
  {
    label: "MAIN",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/szadmin/dashboard" },
      { label: "Users", icon: Users, path: "/szadmin/users", badgeKey: "users", badgeColor: "#2563EB" },
      { label: "Jobs", icon: Briefcase, path: "/szadmin/jobs", badgeKey: "jobs", badgeColor: "#B45309" },
      { label: "Leads", icon: Target, path: "/szadmin/leads" },
    ],
  },
  {
    label: "APPROVALS",
    items: [
      { label: "Job Approval", icon: CheckCircle, path: "/szadmin/job-approval", badgeKey: "jobs", badgeColor: "#B45309" },
      { label: "Payment Release", icon: DollarSign, path: "/szadmin/payment-release", badgeKey: "payments", badgeColor: "#15803D" },
      { label: "Payments", icon: CreditCard, path: "/szadmin/payments" },
    ],
  },
  {
    label: "COMMUNICATION",
    items: [
      { label: "Messages", icon: Send, path: "/szadmin/messages", badgeKey: "messages", badgeColor: "#78287C" },
      { label: "System Messages", icon: MessageSquare, path: "/szadmin/system-messages" },
      { label: "Email Tests", icon: Mail, path: "/szadmin/email-tests" },
      { label: "Email Log", icon: MailOpen, path: "/szadmin/email-log" },
    ],
  },
  {
    label: "SYSTEM",
    items: [
      { label: "Reports", icon: BarChart3, path: "/szadmin/reports" },
      { label: "User Map", icon: MapPin, path: "/szadmin/user-map" },
      { label: "Settings", icon: Settings, path: "/szadmin/settings" },
      { label: "Admin Users", icon: Shield, path: "/szadmin/admins" },
      { label: "Policies", icon: FileText, path: "/szadmin/policies" },
      { label: "Services", icon: Server, path: "/szadmin/services" },
      { label: "Offer Audit Log", icon: ScrollText, path: "/szadmin/offer-audit-log" },
      { label: "Reviews", icon: Star, path: "/szadmin/reviews" },
    ],
  },
];

function NavItem({ item, active, collapsed, count }) {
  const Icon = item.icon;
  const showBadge = item.badgeKey && count > 0;

  return (
    <Link
      to={item.path}
      title={item.label}
      className={active ? "sz-nav-item" : "sz-nav-item sz-nav-idle"}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "9px 10px",
        marginBottom: 2,
        borderRadius: 9,
        textDecoration: "none",
        fontSize: 13.5,
        fontWeight: active ? 600 : 500,
        color: active ? "#fff" : "rgba(255,255,255,.62)",
        background: active ? "linear-gradient(120deg,#572B7C,#78287C)" : "transparent",
        boxShadow: active ? "0 6px 16px -6px rgba(120,40,124,.7)" : "none",
        justifyContent: collapsed ? "center" : "flex-start",
        transition: "background .15s, color .15s",
      }}
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span className="truncate whitespace-nowrap">{item.label}</span>}

      {showBadge && !collapsed && (
        <span
          style={{
            marginLeft: "auto", minWidth: 19, height: 19, padding: "0 5px", borderRadius: 999,
            background: item.badgeColor, color: "#fff", fontSize: 10.5, fontWeight: 700,
            fontFamily: "Poppins, sans-serif", display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {count}
        </span>
      )}
      {showBadge && collapsed && (
        <span
          style={{
            position: "absolute", top: 6, right: 9, width: 8, height: 8, borderRadius: 999,
            background: item.badgeColor, boxShadow: "0 0 0 2px #0C1120",
          }}
        />
      )}
    </Link>
  );
}

export default function AdminSidebar() {
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useAdminSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ users: 0, jobs: 0, payments: 0, messages: 0 });

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // Live count badges — fail silently per-endpoint so a single failure never blanks the rail.
  useEffect(() => {
    let alive = true;
    const num = (v) => (Array.isArray(v) ? v.length : Number(v) || 0);
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('shootzu_admin_token')}` } };
    (async () => {
      const [pendingUsers, pendingJobs, releaseJobs, convos] = await Promise.allSettled([
        axios.get(`${BACKEND}/api/admin/pending-approvals`, auth),
        axios.get(`${BACKEND}/api/admin/jobs/pending-approval`, auth),
        axios.get(`${BACKEND}/api/admin/jobs/release-payment`, auth),
        axios.get(`${BACKEND}/api/admin/messages/conversations`, auth),
      ]);
      if (!alive) return;
      const usersCount = pendingUsers.status === "fulfilled"
        ? num(pendingUsers.value.data?.users ?? pendingUsers.value.data?.pending_users ?? pendingUsers.value.data)
        : 0;
      const jobsCount = pendingJobs.status === "fulfilled"
        ? num(pendingJobs.value.data?.jobs ?? pendingJobs.value.data)
        : 0;
      const paymentsCount = releaseJobs.status === "fulfilled"
        ? num(releaseJobs.value.data?.jobs ?? releaseJobs.value.data)
        : 0;
      const messagesCount = convos.status === "fulfilled"
        ? (convos.value.data?.conversations || convos.value.data || []).reduce(
            (s, c) => s + (c.unread_count || 0), 0)
        : 0;
      setCounts({ users: usersCount, jobs: jobsCount, payments: paymentsCount, messages: messagesCount });
    })();
    return () => { alive = false; };
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("shootzu_admin");
    navigate("/szadmin");
  };

  const isActive = (path) => location.pathname === path;

  const rail = (
    <div className="flex flex-col h-full" style={{ background: "#0C1120", color: "#fff" }}>
      {/* Logo / collapse */}
      <div className="flex items-center justify-between gap-2.5" style={{ padding: "18px 16px 16px" }}>
        {!collapsed ? (
          <>
            <Link to="/szadmin/dashboard" className="min-w-0">
              <img src="/assets/szadmin/horizontal-white.png" alt="Shootzu" style={{ height: 46, width: "auto" }} />
            </Link>
            <button
              onClick={toggleCollapsed}
              title="Collapse"
              className="hidden lg:flex items-center justify-center shrink-0"
              style={{ width: 26, height: 26, border: "none", borderRadius: 7, background: "rgba(255,255,255,.07)", color: "rgba(255,255,255,.6)", cursor: "pointer", fontSize: 13 }}
            >
              «
            </button>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1.5 rounded-lg"
              style={{ color: "rgba(255,255,255,.6)" }}
            >
              <X className="w-5 h-5" />
            </button>
          </>
        ) : (
          <img
            src="/assets/szadmin/symbol-white.svg"
            alt="Shootzu — expand menu"
            title="Expand menu"
            onClick={toggleCollapsed}
            style={{ height: 42, width: "auto", margin: "0 auto", cursor: "pointer" }}
          />
        )}
      </div>

      {/* Nav */}
      <nav className="szscroll flex-1 overflow-y-auto" style={{ padding: "6px 12px 12px" }}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.label} style={{ marginBottom: 14 }}>
            {!collapsed && (
              <div
                style={{
                  fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: 10,
                  letterSpacing: 1.4, color: "rgba(255,255,255,.34)", padding: "8px 10px 6px",
                }}
              >
                {section.label}
              </div>
            )}
            {section.items.map((item) => (
              <NavItem
                key={item.path}
                item={item}
                active={isActive(item.path)}
                collapsed={collapsed}
                count={item.badgeKey ? counts[item.badgeKey] : 0}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div style={{ padding: 12, borderTop: "1px solid rgba(255,255,255,.08)" }}>
        <button
          onClick={handleLogout}
          className="sz-logout"
          style={{
            display: "flex", alignItems: "center", gap: 11, width: "100%",
            padding: "9px 10px", borderRadius: 9, background: "transparent", border: "none",
            cursor: "pointer", fontSize: 13.5, fontWeight: 600, color: "rgba(255,255,255,.6)",
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Log out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger — hidden while the rail is open (the rail has its own ✕) */}
      {!mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed left-3 top-3 z-[210] lg:hidden flex items-center justify-center"
          style={{
            width: 40, height: 40, border: "1px solid #E6E9EF", borderRadius: 11, background: "#fff",
            boxShadow: "0 4px 14px -4px rgba(12,17,32,.25)", color: "#0C1120",
          }}
          title="Menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[150] lg:hidden"
          style={{ background: "rgba(12,17,32,.5)" }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile rail */}
      <aside
        className={`fixed inset-y-0 left-0 z-[200] lg:hidden transition-transform duration-[260ms] ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ width: 250, boxShadow: "0 0 50px rgba(0,0,0,.4)" }}
      >
        {rail}
      </aside>

      {/* Desktop rail */}
      <aside
        className="hidden lg:block fixed inset-y-0 left-0 z-30 transition-[width] duration-[260ms]"
        style={{ width: collapsed ? 66 : 248 }}
      >
        {rail}
      </aside>
    </>
  );
}
